import {
  BotCategorizationData,
  BotEnvironmentData,
  BotFlow,
  BotId
} from '@feedyou-ai/feedbot-schema'
import { VersionIncrement } from '@feedyou/utils'

import { botsApi } from './api/botsApi'
import { openBotIdSelector } from '../../store/app/selectors'
import {
  isUnsavedSelector,
  lastVersionStringForCurrentBotSelector
} from '../../store/socketServer/selectors'
import { authorForCurrentUserSelector } from '../../store/user'

import { alertHavingUnsavedChanges } from './utils/bootbox'
import { featureValueSelector } from '../../store/features/selectors'
import {
  createMutationHelper,
  createQueryHelper,
  includeInKey,
  someAreFalsy
} from '../../lib/rtkq/endpointHelpers'

import { environmentGroupForCurrentBotSelector } from './selectors/environmentGroupForCurrentBotSelector'
import { WizardConfiguration } from './types'

const endpoints = botsApi.endpoints

const getBotsMetadata = createQueryHelper(endpoints.getBotsMetadata).addArgsTransformer<void>(
  state => {
    return { fromBotId: openBotIdSelector(state) }
  }
)

// This approach (always fetching original version by specific version string) ensures that
// originalVersion is ALWAYS in sync with latest socket server version.
//
// You can provide `customVersion` in contexts where you are not connected to socket server
// ... but don't do that anywhere else, unless you have a good reason :)
const getOriginalVersionFlowForCurrentBot = createQueryHelper(endpoints.getBotFlow)
  .addSkipCondition(someAreFalsy('id', 'version'))
  .addArgsTransformer<{ customVersion?: string } | void>((state, params) => {
    return {
      id: openBotIdSelector(state)!,
      version: (params && params?.customVersion) || lastVersionStringForCurrentBotSelector(state)!
    }
  })

const getFlowForCurrentBot = createQueryHelper(endpoints.getBotFlow)
  .addSkipCondition(someAreFalsy('id', 'version'))
  .addArgsTransformer<{ version: string }>((state, { version }) => {
    const id = openBotIdSelector(state)!
    return { id, version }
  })

const getLatestSemverForCurrentBot = createQueryHelper(endpoints.getLatestSemver)
  .addSkipCondition(someAreFalsy('id'))
  .addArgsTransformer<void>((state, callSiteArgs) => {
    return { id: openBotIdSelector(state)! }
  })

const getConfigurationForCurrentBot = createQueryHelper(endpoints.getBotConfiguration)
  .addSkipCondition(someAreFalsy('id'))
  .addArgsTransformer<void>((state, callSiteArgs) => {
    return { id: openBotIdSelector(state)! }
  })

const getVersionListPageForCurrentBot = createQueryHelper(endpoints.getVersionListPageForBot)
  .addSkipCondition(someAreFalsy('botId', 'limit', 'pageNr'))
  .addArgsTransformer<{ pageNr: number }>((state, callSiteArgs) => {
    return {
      botId: openBotIdSelector(state)!,
      limit: 30,
      pageNr: callSiteArgs.pageNr
    }
  })

const getCurrentUserPermissionsForCurrentBot = createQueryHelper(
  endpoints.getCurrentUserPermissionsForBot
).addArgsTransformer<void>((state, callSiteArgs) => {
  return { botId: openBotIdSelector(state)! }
})

export const getVersionDiff = createQueryHelper(endpoints.getVersionDiff)

const createNewBot = createMutationHelper(endpoints.createNewBot)
  .addSkipCondition(someAreFalsy('id', 'categorization'))
  .addArgsTransformer<{ id: BotId; categorization: BotCategorizationData }>(
    (state, callSiteArgs) => {
      return {
        ...callSiteArgs,
        author: authorForCurrentUserSelector(state),
        augmentationFlags: {
          newStorageValidator: !!featureValueSelector('NEW_VALIDATOR_STORAGE_MODE')(state)
        }
      }
    }
  )

const createNewBotVersionForCurrentBot = createMutationHelper(endpoints.createNewVersion)
  .addSkipCondition(someAreFalsy('id', 'increment', 'flow'))
  .addArgsTransformer<{ increment: VersionIncrement; flow: BotFlow; versionTitle: string }>(
    (state, callSiteArgs) => {
      return {
        ...callSiteArgs,
        id: openBotIdSelector(state)!,
        author: authorForCurrentUserSelector(state)
      }
    }
  )

const duplicateCurrentBotToNewBot = createMutationHelper(endpoints.duplicateToNewBot)
  .addSkipCondition(someAreFalsy('sourceBotId', 'sourceVersion'))
  .addArgsTransformer<{
    newBotId: BotId
    categorization: BotCategorizationData
    versionTitle: string
    environmentData?: BotEnvironmentData
    newBotInitialVersion?: string
  }>((state, callSiteArgs) => {
    return {
      ...callSiteArgs,
      environmentLink: callSiteArgs.environmentData,
      author: authorForCurrentUserSelector(state)!,
      sourceBotId: openBotIdSelector(state)!,
      newBotInitialVersion: callSiteArgs.newBotInitialVersion,
      sourceVersion: lastVersionStringForCurrentBotSelector(state)!
    }
  })

const moveTestToProd = createMutationHelper(endpoints.moveTestToProd)
  .addSkipCondition(someAreFalsy('environmentGroupId', 'sourceTestId', 'targetProdId'))
  .addArgsTransformer<void>(state => {
    const { testId, prodId, environmentGroupId } = environmentGroupForCurrentBotSelector(state)

    return {
      environmentGroupId: environmentGroupId!,
      sourceTestId: testId!,
      targetProdId: prodId!,
      author: authorForCurrentUserSelector(state)!
    }
  })

const revertCurrentBot = createMutationHelper(endpoints.revertToVersion)
  .addSkipCondition(someAreFalsy('botId', 'targetVersion'))
  .addArgsTransformer<{ targetVersion: string }>((state, params) => {
    const currentBotId = openBotIdSelector(state)

    return {
      botId: currentBotId!,
      targetVersion: params.targetVersion,
      author: authorForCurrentUserSelector(state)!
    }
  })

const moveProdToTest = createMutationHelper(endpoints.moveProdToTest)
  .addSkipCondition(someAreFalsy('environmentGroupId', 'sourceProdId', 'targetTestId'))
  .addArgsTransformer<void>(state => {
    const { testId, prodId, environmentGroupId } = environmentGroupForCurrentBotSelector(state)

    return {
      environmentGroupId: environmentGroupId!,
      sourceProdId: prodId!,
      targetTestId: testId!,
      author: authorForCurrentUserSelector(state)!
    }
  })

const createTestEnvironmentForCurrentGroup = createMutationHelper(endpoints.createTestEnvironment)
  .addSkipCondition(someAreFalsy('environmentGroupId', 'sourceProdId', 'newBotId'))
  .addArgsTransformer<void>(state => {
    const { prodId, environmentGroupId } = environmentGroupForCurrentBotSelector(state)

    return {
      environmentGroupId: environmentGroupId!,
      sourceProdId: prodId!,
      newBotId: prodId + '-test',
      author: authorForCurrentUserSelector(state)!
    }
  })

const updateBotConfiguration = createMutationHelper(endpoints.updateBotConfiguration)

const duplicateCurrentVersionToDifferentBot = createMutationHelper(endpoints.adoptExistingVersion)
  .addSkipCondition((params, state) => {
    if (isUnsavedSelector(state)) {
      alertHavingUnsavedChanges()
      return true
    }

    return someAreFalsy('sourceBotId', 'sourceVersion', 'targetBotId')(params as any)
  })
  .addArgsTransformer<{ targetBotId: BotId; versionTitle: string | undefined }>(
    (state, callSiteArgs) => {
      return {
        ...callSiteArgs,
        sourceBotId: openBotIdSelector(state)!,
        sourceVersion: lastVersionStringForCurrentBotSelector(state)!,
        author: authorForCurrentUserSelector(state)!
      }
    }
  )

const adoptVersionFromDifferentBot = createMutationHelper(endpoints.adoptExistingVersion)
  .addSkipCondition(someAreFalsy('sourceBotId', 'targetBotId'))
  .addArgsTransformer<{
    targetBotId: BotId
    sourceBotId: BotId
    versionTitle?: string
    sourceVersion?: string
    versionIncrement?: VersionIncrement | 'adopt_source'
  }>((state, callSiteArgs) => {
    return { ...callSiteArgs, author: authorForCurrentUserSelector(state) }
  })

const disableEnvironment = createMutationHelper(endpoints.disable).addSkipCondition(
  someAreFalsy('id')
)
const enableEnvironment = createMutationHelper(endpoints.enable).addSkipCondition(
  someAreFalsy('id')
)

const archiveBot = createMutationHelper(endpoints.archive)
  .addSkipCondition(someAreFalsy('botId'))
  .addFixedCacheKeyGetter(includeInKey('botId'))
  .addArgsTransformer<{ botId: BotId }>((state, callSiteArgs) => {
    return { ...callSiteArgs, author: authorForCurrentUserSelector(state)! }
  })

const deleteBot = createMutationHelper(endpoints.delete)
  .addSkipCondition(someAreFalsy('botId'))
  .addFixedCacheKeyGetter(includeInKey('botId'))
  .addArgsTransformer<{ botId: BotId; force?: boolean }>((state, callSiteArgs) => {
    return { ...callSiteArgs, author: authorForCurrentUserSelector(state)! }
  })

const restoreBot = createMutationHelper(endpoints.restore)
  .addSkipCondition(someAreFalsy('botId'))
  .addFixedCacheKeyGetter(includeInKey('botId'))
  .addArgsTransformer<{ botId: BotId }>((state, callSiteArgs) => {
    return { ...callSiteArgs, author: authorForCurrentUserSelector(state)! }
  })

const getWizardConfigurationForCurrentBot = createQueryHelper(
  endpoints.getWizardConfiguration
)
  .addSkipCondition(someAreFalsy('id'))
  .addArgsTransformer<void>((state, callSiteArgs) => {
    return { id: openBotIdSelector(state)! }
  })

const updateWizardConfigurationForCurrentBot = createMutationHelper(
  endpoints.updateWizardConfiguration
)
  .addSkipCondition(someAreFalsy('id'))
  .addArgsTransformer<{ wizardData: WizardConfiguration }>((state, callSiteArgs) => {
    return { id: openBotIdSelector(state)!, wizardData: callSiteArgs.wizardData }
  })

export const endpointHelpers = {
  getBotsMetadata,
  getCurrentUserPermissionsForCurrentBot,
  getVersionListPageForCurrentBot,
  getVersionDiff,
  getOriginalVersionFlowForCurrentBot,
  getFlowForCurrentBot,

  getLatestSemverForCurrentBot,
  getConfigurationForCurrentBot,

  createTestEnvironmentForCurrentBot: createTestEnvironmentForCurrentGroup,
  moveTestToProd,
  moveProdToTest,
  revertCurrentBot,

  updateBotConfiguration,
  duplicateCurrentBotToNewBot,
  duplicateCurrentVersionToDifferentBot,
  createNewBot,
  createNewBotVersionForCurrentBot,
  enableEnvironment,
  disableEnvironment,
  adoptVersionFromDifferentBot,

  archiveBot,
  deleteBot,
  restoreBot,

  getWizardConfigurationForCurrentBot,
  updateWizardConfigurationForCurrentBot
}
